import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

const emptyForm = () => ({
  name: '',
  shortName: '',
  currency: 'ARS',
  minAmount: '10000',
  maxAmount: '500000',
  minTermMonths: '3',
  maxTermMonths: '36',
  interestRate: '0.15',
  tea: '',
  adminFeePercent: '0',
  installmentAdminFixed: '350',
  installmentAdminPercent: '20',
  installmentAdminCap: '40000',
  quotaPercent: '0.6435',
  remainderPercent: '0.6435',
  monthsToFirstDue: '2',
  planCode: '',
  planType: 'FIJO',
  graceDays: '0',
  fixedDueDay: '10',
  penaltyStartDay: '20',
  allowParallelLoan: false,
  minOverdueInstallmentsForRefinance: '',
  minCancelledLoansRequired: '0',
  branchIds: [] as string[],
});

function fmtDate(d: string | Date | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-AR');
}

export function LoanProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [discountCodes, setDiscountCodes] = useState<any[]>([]);
  const [codeForm, setCodeForm] = useState({ code: '', description: '', percentOff: '10' });
  const [codeBusy, setCodeBusy] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const [validityForm, setValidityForm] = useState({ validFrom: '', validTo: '', notes: '' });
  const [coeffRows, setCoeffRows] = useState<{ termMonths: string; coefficient: string }[]>([
    { termMonths: '12', coefficient: '1' },
  ]);
  const [chargeRows, setChargeRows] = useState<
    {
      code: string;
      name: string;
      kind: string;
      valueMode: 'FIXED' | 'PERCENT';
      base: 'INSTALLMENT' | 'LOAN_PRINCIPAL';
      value: string;
      isBalancing: boolean;
      isActive: boolean;
    }[]
  >([]);
  const [simForm, setSimForm] = useState({ amount: '50000', termMonths: '12', startDate: '' });
  const [simResult, setSimResult] = useState<any | null>(null);
  const [simBusy, setSimBusy] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [prods, codes, brs] = await Promise.all([
        apiService.getLoanProducts({ activeOnly: false }),
        apiService.listDiscountCodes(),
        apiService.getBranches(true),
      ]);
      setProducts(prods);
      setDiscountCodes(codes);
      setBranches(brs);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id: string) => {
    setDetailId(id);
    setDetailBusy(true);
    setError(null);
    try {
      const p = await apiService.getLoanProduct(id);
      setDetail(p);
      setSelectedBranches(p.branches?.map((b: any) => b.branchId) ?? []);
      setCoeffRows(
        p.coefficients?.length
          ? p.coefficients.map((c: any) => ({
              termMonths: String(c.termMonths),
              coefficient: String(c.coefficient),
            }))
          : [{ termMonths: '12', coefficient: '1' }],
      );
      setChargeRows(
        (p.charges ?? []).map((c: any) => ({
          code: c.code,
          name: c.name,
          kind: c.kind ?? 'CUSTOM',
          valueMode: c.valueMode ?? 'PERCENT',
          base: c.base ?? 'INSTALLMENT',
          value: String(c.value ?? 0),
          isBalancing: !!c.isBalancing,
          isActive: c.isActive !== false,
        })),
      );
      setValidityForm({ validFrom: '', validTo: '', notes: '' });
      setSimResult(null);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
      setDetailId(null);
    } finally {
      setDetailBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await apiService.createLoanProduct({
        name: form.name,
        shortName: form.shortName || undefined,
        minAmount: 0,
        maxAmount: 999_999_999,
        minTermMonths: parseInt(form.minTermMonths, 10),
        maxTermMonths: parseInt(form.maxTermMonths, 10),
        interestRate: parseFloat(form.interestRate),
        tea: form.tea ? parseFloat(form.tea) : undefined,
        adminFeePercent: form.adminFeePercent ? parseFloat(form.adminFeePercent) : undefined,
        installmentAdminFixed: form.installmentAdminFixed ? parseFloat(form.installmentAdminFixed) : 0,
        installmentAdminPercent: form.installmentAdminPercent ? parseFloat(form.installmentAdminPercent) : 0,
        installmentAdminCap: form.installmentAdminCap ? parseFloat(form.installmentAdminCap) : null,
        quotaPercent: form.quotaPercent ? parseFloat(form.quotaPercent) : undefined,
        remainderPercent: form.remainderPercent ? parseFloat(form.remainderPercent) : undefined,
        monthsToFirstDue: form.monthsToFirstDue ? parseInt(form.monthsToFirstDue, 10) : 1,
        currency: form.currency,
        planCode: form.planCode || undefined,
        planType: form.planType || undefined,
        graceDays: parseInt(form.graceDays, 10) || 0,
        fixedDueDay: form.fixedDueDay ? parseInt(form.fixedDueDay, 10) : undefined,
        penaltyStartDay: form.penaltyStartDay ? parseInt(form.penaltyStartDay, 10) : undefined,
        allowParallelLoan: form.allowParallelLoan,
        minOverdueInstallmentsForRefinance: form.minOverdueInstallmentsForRefinance
          ? parseInt(form.minOverdueInstallmentsForRefinance, 10)
          : undefined,
        minCancelledLoansRequired: parseInt(form.minCancelledLoansRequired, 10) || 0,
        branchIds: form.branchIds.length ? form.branchIds : undefined,
      });
      setShowForm(false);
      setForm(emptyForm());
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (p: any) => {
    setActingOn(p.id);
    setError(null);
    try {
      if (p.isActive) await apiService.deactivateLoanProduct(p.id);
      else await apiService.activateLoanProduct(p.id);
      await load();
      if (detailId === p.id) await openDetail(p.id);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setActingOn(null);
    }
  };

  const saveDetailParams = async () => {
    if (!detail) return;
    setDetailBusy(true);
    setError(null);
    try {
      await apiService.updateLoanProduct(detail.id, {
        name: detail.name,
        shortName: detail.shortName ?? undefined,
        minTermMonths: Number(detail.minTermMonths),
        maxTermMonths: Number(detail.maxTermMonths),
        interestRate: Number(detail.interestRate),
        tea: detail.tea != null && detail.tea !== '' ? Number(detail.tea) : undefined,
        adminFeePercent: detail.adminFeePercent != null ? Number(detail.adminFeePercent) : undefined,
        installmentAdminFixed: detail.installmentAdminFixed != null && detail.installmentAdminFixed !== ''
          ? Number(detail.installmentAdminFixed)
          : 0,
        installmentAdminPercent: detail.installmentAdminPercent != null && detail.installmentAdminPercent !== ''
          ? Number(detail.installmentAdminPercent)
          : 0,
        installmentAdminCap: detail.installmentAdminCap != null && detail.installmentAdminCap !== ''
          ? Number(detail.installmentAdminCap)
          : null,
        quotaPercent: detail.quotaPercent != null && detail.quotaPercent !== '' ? Number(detail.quotaPercent) : undefined,
        remainderPercent: detail.remainderPercent != null && detail.remainderPercent !== '' ? Number(detail.remainderPercent) : undefined,
        monthsToFirstDue: detail.monthsToFirstDue != null && detail.monthsToFirstDue !== ''
          ? Number(detail.monthsToFirstDue)
          : 1,
        currency: detail.currency,
        planCode: detail.planCode || undefined,
        planType: detail.planType,
        graceDays: Number(detail.graceDays) || 0,
        fixedDueDay: detail.fixedDueDay ? Number(detail.fixedDueDay) : undefined,
        penaltyStartDay: detail.penaltyStartDay ? Number(detail.penaltyStartDay) : undefined,
        allowParallelLoan: Boolean(detail.allowParallelLoan),
        minOverdueInstallmentsForRefinance: detail.minOverdueInstallmentsForRefinance != null && detail.minOverdueInstallmentsForRefinance !== ''
          ? Number(detail.minOverdueInstallmentsForRefinance)
          : undefined,
        minCancelledLoansRequired: Number(detail.minCancelledLoansRequired) || 0,
        installmentPricingMode: detail.installmentPricingMode === 'BREAKDOWN' ? 'BREAKDOWN' : 'FORMULA',
      });
      await openDetail(detail.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const addValidity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detail) return;
    setDetailBusy(true);
    setError(null);
    try {
      await apiService.addProductValidity(detail.id, {
        validFrom: validityForm.validFrom,
        validTo: validityForm.validTo || undefined,
        notes: validityForm.notes || undefined,
      });
      setValidityForm({ validFrom: '', validTo: '', notes: '' });
      await openDetail(detail.id);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const endValidity = async (validityId: string) => {
    if (!detail) return;
    setDetailBusy(true);
    setError(null);
    try {
      await apiService.endProductValidity(detail.id, validityId);
      await openDetail(detail.id);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const saveBranches = async () => {
    if (!detail) return;
    setDetailBusy(true);
    setError(null);
    try {
      await apiService.setProductBranches(detail.id, selectedBranches);
      await openDetail(detail.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const saveCoefficients = async () => {
    if (!detail) return;
    setDetailBusy(true);
    setError(null);
    try {
      const coefficients = coeffRows
        .filter((r) => r.termMonths && r.coefficient)
        .map((r) => ({
          termMonths: parseInt(r.termMonths, 10),
          coefficient: parseFloat(r.coefficient),
        }));
      await apiService.setProductCoefficients(detail.id, coefficients);
      await openDetail(detail.id);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const saveCharges = async () => {
    if (!detail) return;
    setDetailBusy(true);
    setError(null);
    try {
      const charges = chargeRows
        .filter((r) => r.code.trim() && r.name.trim())
        .map((r, i) => ({
          code: r.code.trim().toUpperCase(),
          name: r.name.trim(),
          kind: (r.kind === 'SYSTEM' ? 'SYSTEM' : 'CUSTOM') as 'SYSTEM' | 'CUSTOM',
          valueMode: r.valueMode,
          base: r.base,
          value: parseFloat(r.value) || 0,
          isBalancing: r.isBalancing,
          sortOrder: i,
          isActive: r.isActive,
        }));
      await apiService.setProductCharges(detail.id, charges);
      await openDetail(detail.id);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const seedSystemCharges = () => {
    setChargeRows([
      { code: 'INTEREST', name: 'Interés', kind: 'SYSTEM', valueMode: 'PERCENT', base: 'INSTALLMENT', value: '4', isBalancing: false, isActive: true },
      { code: 'ADMIN', name: 'Gastos admin', kind: 'SYSTEM', valueMode: 'PERCENT', base: 'INSTALLMENT', value: '3', isBalancing: false, isActive: true },
      { code: 'INSURANCE', name: 'Seguro', kind: 'SYSTEM', valueMode: 'PERCENT', base: 'INSTALLMENT', value: '2', isBalancing: false, isActive: true },
      { code: 'CAPITAL', name: 'Capital', kind: 'SYSTEM', valueMode: 'PERCENT', base: 'INSTALLMENT', value: '0', isBalancing: true, isActive: true },
    ]);
  };

  const toggleBranch = (id: string) => {
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(id) ? f.branchIds.filter((x) => x !== id) : [...f.branchIds, id],
    }));
  };

  const runSimulation = async () => {
    if (!detail) return;
    setSimBusy(true);
    setError(null);
    try {
      const result = await apiService.simulateLoanProduct(detail.id, {
        amount: parseFloat(simForm.amount),
        termMonths: parseInt(simForm.termMonths, 10),
        startDate: simForm.startDate || undefined,
      });
      setSimResult(result);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSimBusy(false);
    }
  };

  const toggleDetailBranch = (id: string) => {
    setSelectedBranches((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const field = (
    label: string,
    value: string | number,
    onChange: (v: string) => void,
    type = 'text',
    hint?: string,
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        step={type === 'number' ? 'any' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Planes</h1>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {showForm ? 'Cancelar' : '+ Nuevo plan'}
        </button>
      </div>

      {error && !detailId && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {showForm && (
        <form
          onSubmit={submit}
          className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {field('Nombre', form.name, (v) => setForm({ ...form, name: v }))}
          {field('Nombre corto', form.shortName, (v) => setForm({ ...form, shortName: v }))}
          {field('Moneda', form.currency, (v) => setForm({ ...form, currency: v }))}
          {field('Plazo mínimo (meses)', form.minTermMonths, (v) => setForm({ ...form, minTermMonths: v }), 'number')}
          {field('Plazo máximo (meses)', form.maxTermMonths, (v) => setForm({ ...form, maxTermMonths: v }), 'number')}
          <p className="text-xs text-gray-500 md:col-span-2 -mt-2">
            Podés usar el mismo valor en mín. y máx. para un plazo fijo. El monto no se valida al otorgar.
          </p>
          {field('Tasa anual (decimal)', form.interestRate, (v) => setForm({ ...form, interestRate: v }), 'number', 'ej. 0.15 = 15%')}
          {field('TEA (decimal)', form.tea, (v) => setForm({ ...form, tea: v }), 'number', 'Opcional; si vacío usa tasa anual')}
          {field('Gastos admin. % (capital)', form.adminFeePercent, (v) => setForm({ ...form, adminFeePercent: v }), 'number', 'Legado: % financiado sobre capital')}
          {field('Gasto admin fijo / cuota', form.installmentAdminFixed, (v) => setForm({ ...form, installmentAdminFixed: v }), 'number', 'ej. 350 — línea aparte')}
          {field('% gasto admin / cuota', form.installmentAdminPercent, (v) => setForm({ ...form, installmentAdminPercent: v }), 'number', 'ej. 20')}
          {field('Tope gasto admin variable', form.installmentAdminCap, (v) => setForm({ ...form, installmentAdminCap: v }), 'number', 'ej. 40000')}
          {field('Coef. cuota 1', form.quotaPercent, (v) => setForm({ ...form, quotaPercent: v }), 'number', 'ej. 0.6435')}
          {field('Coef. resto', form.remainderPercent, (v) => setForm({ ...form, remainderPercent: v }), 'number', 'ej. 0.6435')}
          {field('Meses hasta 1ª cuota', form.monthsToFirstDue, (v) => setForm({ ...form, monthsToFirstDue: v }), 'number', 'ej. 2')}
          {field('Código plan', form.planCode, (v) => setForm({ ...form, planCode: v }), 'text', 'ej. *F12')}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de plan</label>
            <select
              value={form.planType}
              onChange={(e) => setForm({ ...form, planType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="FIJO">FIJO (día fijo)</option>
              <option value="VARIABLE">VARIABLE</option>
            </select>
          </div>
          {field('Días de gracia', form.graceDays, (v) => setForm({ ...form, graceDays: v }), 'number')}
          {form.planType === 'FIJO' && field('Día fijo venc. (1-28)', form.fixedDueDay, (v) => setForm({ ...form, fixedDueDay: v }), 'number')}
          {field('Día inicio punitorio', form.penaltyStartDay, (v) => setForm({ ...form, penaltyStartDay: v }), 'number', 'ej. 20')}
          {field('Cuotas impagas refin.', form.minOverdueInstallmentsForRefinance, (v) => setForm({ ...form, minOverdueInstallmentsForRefinance: v }), 'number')}
          {field('Prést. cancelados req.', form.minCancelledLoansRequired, (v) => setForm({ ...form, minCancelledLoansRequired: v }), 'number')}
          <div className="flex items-center gap-2 pt-6">
            <input
              id="allowParallel"
              type="checkbox"
              checked={form.allowParallelLoan}
              onChange={(e) => setForm({ ...form, allowParallelLoan: e.target.checked })}
            />
            <label htmlFor="allowParallel" className="text-sm text-gray-700">Permite préstamo paralelo</label>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Sucursales habilitadas (vacío = todas)</label>
            <div className="flex flex-wrap gap-3">
              {branches.map((b) => (
                <label key={b.id} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={form.branchIds.includes(b.id)}
                    onChange={() => toggleBranch(b.id)}
                  />
                  {b.code} — {b.name}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
              Cancelar
            </button>
            <button type="submit" disabled={creating} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {creating ? 'Creando…' : 'Crear plan'}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando…</div>
        ) : products.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No hay productos. Creá el primero.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Moneda</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montos</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Plazo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">TEA/Tasa</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Vigencia</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Activo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((p) => {
                const vig = p.validities?.[0];
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <span className="font-medium">{p.name}</span>
                      {p.planCode && <span className="block font-mono text-xs text-gray-500">{p.planCode}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {p.planType ?? '—'}
                      {p.fixedDueDay && <span className="block text-xs">Día {p.fixedDueDay}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">{p.currency}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      ${Number(p.minAmount ?? 0).toLocaleString()} – ${Number(p.maxAmount ?? 0).toLocaleString()}
                      <span className="block text-[10px] text-gray-400 font-normal">informativo</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">{p.minTermMonths}–{p.maxTermMonths} m</td>
                    <td className="px-4 py-3 text-sm text-right">
                      {((p.tea ?? p.interestRate) * 100).toFixed(2)}%
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-600">
                      {vig ? (
                        <span className="text-xs">{fmtDate(vig.validFrom)} – {fmtDate(vig.validTo)}</span>
                      ) : (
                        <span className="text-xs text-gray-400">Sin vigencia</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">{p.isActive ? <span className="text-green-700">●</span> : <span className="text-gray-300">●</span>}</td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                      <button onClick={() => openDetail(p.id)} className="text-blue-700 hover:text-blue-900 mr-3">Parametrizar</button>
                      <button
                        disabled={actingOn === p.id}
                        onClick={() => toggleActive(p)}
                        className={(p.isActive ? 'text-red-700' : 'text-green-700') + ' disabled:opacity-50'}
                      >
                        {p.isActive ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detailId && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 bg-white z-10 rounded-t-lg">
              <h2 className="text-xl font-semibold">{detail?.name ?? 'Cargando…'}</h2>
              <button onClick={() => { setDetailId(null); setDetail(null); setError(null); }} className="text-gray-500 hover:text-gray-800">✕</button>
            </div>
            {error && (
              <div className="mx-6 mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}
            {detailBusy && !detail ? (
              <div className="p-12 text-center text-gray-500">Cargando…</div>
            ) : detail && (
              <div className="p-6 space-y-8">
                <section>
                  <h3 className="text-lg font-medium mb-4">Parámetros del plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {field('Nombre', detail.name, (v) => setDetail({ ...detail, name: v }))}
                    {field('Moneda', detail.currency, (v) => setDetail({ ...detail, currency: v }))}
                    {field('Plazo mín.', detail.minTermMonths, (v) => setDetail({ ...detail, minTermMonths: v }), 'number')}
                    {field('Plazo máx.', detail.maxTermMonths, (v) => setDetail({ ...detail, maxTermMonths: v }), 'number')}
                    <p className="text-xs text-gray-500 md:col-span-2">
                      Mismo mín. y máx. = plazo fijo. Montos sin validación al otorgar.
                    </p>
                    {field('Tasa anual (decimal)', detail.interestRate, (v) => setDetail({ ...detail, interestRate: v }), 'number', '0 a 1. Ej: 0.15 = 15%. Con coeficiente usá 0')}
                    {field('TEA (decimal)', detail.tea ?? '', (v) => setDetail({ ...detail, tea: v }), 'number', '0 a 1. Si vacío usa la tasa')}
                    {field('Gastos admin % (capital)', detail.adminFeePercent ?? 0, (v) => setDetail({ ...detail, adminFeePercent: v }), 'number')}
                    {field('Gasto admin fijo / cuota', detail.installmentAdminFixed ?? 0, (v) => setDetail({ ...detail, installmentAdminFixed: v }), 'number')}
                    {field('% gasto admin / cuota', detail.installmentAdminPercent ?? 0, (v) => setDetail({ ...detail, installmentAdminPercent: v }), 'number')}
                    {field('Tope gasto admin', detail.installmentAdminCap ?? '', (v) => setDetail({ ...detail, installmentAdminCap: v }), 'number')}
                    {field('Coef. cuota 1', detail.quotaPercent ?? '', (v) => setDetail({ ...detail, quotaPercent: v }), 'number')}
                    {field('Coef. resto', detail.remainderPercent ?? '', (v) => setDetail({ ...detail, remainderPercent: v }), 'number')}
                    {field('Meses hasta 1ª cuota', detail.monthsToFirstDue ?? 1, (v) => setDetail({ ...detail, monthsToFirstDue: v }), 'number')}
                    {field('Días gracia', detail.graceDays ?? 0, (v) => setDetail({ ...detail, graceDays: v }), 'number')}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo plan</label>
                      <select
                        value={detail.planType ?? 'VARIABLE'}
                        onChange={(e) => setDetail({ ...detail, planType: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                      >
                        <option value="FIJO">FIJO</option>
                        <option value="VARIABLE">VARIABLE</option>
                      </select>
                    </div>
                    {field('Día fijo venc.', detail.fixedDueDay ?? '', (v) => setDetail({ ...detail, fixedDueDay: v }), 'number')}
                    {field('Día inicio punitorio', detail.penaltyStartDay ?? '', (v) => setDetail({ ...detail, penaltyStartDay: v }), 'number')}
                    {field('Cuotas impagas refin.', detail.minOverdueInstallmentsForRefinance ?? '', (v) => setDetail({ ...detail, minOverdueInstallmentsForRefinance: v }), 'number')}
                    {field('Prést. cancelados', detail.minCancelledLoansRequired ?? 0, (v) => setDetail({ ...detail, minCancelledLoansRequired: v }), 'number')}
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        type="checkbox"
                        checked={Boolean(detail.allowParallelLoan)}
                        onChange={(e) => setDetail({ ...detail, allowParallelLoan: e.target.checked })}
                      />
                      <span className="text-sm">Préstamo paralelo</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modo de cuota</label>
                      <select
                        value={detail.installmentPricingMode ?? 'FORMULA'}
                        onChange={(e) => setDetail({ ...detail, installmentPricingMode: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                      >
                        <option value="FORMULA">Fórmula (TEA/coeficiente) + desglose</option>
                        <option value="BREAKDOWN">Solo desglose configurable</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      disabled={detailBusy}
                      onClick={saveDetailParams}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
                    >
                      Guardar parámetros
                    </button>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-medium mb-4">Vigencias</h3>
                  <table className="min-w-full text-sm border rounded-lg overflow-hidden mb-4">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Desde</th>
                        <th className="px-3 py-2 text-left">Hasta</th>
                        <th className="px-3 py-2 text-center">Activa</th>
                        <th className="px-3 py-2 text-right">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(detail.validities ?? []).map((v: any) => (
                        <tr key={v.id}>
                          <td className="px-3 py-2">{fmtDate(v.validFrom)}</td>
                          <td className="px-3 py-2">{fmtDate(v.validTo)}</td>
                          <td className="px-3 py-2 text-center">{v.isActive ? 'Sí' : 'No'}</td>
                          <td className="px-3 py-2 text-right">
                            {v.isActive && (
                              <button
                                disabled={detailBusy}
                                onClick={() => endValidity(v.id)}
                                className="text-red-700 hover:underline disabled:opacity-50"
                              >
                                Finalizar vigencia
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <form onSubmit={addValidity} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Fecha desde</label>
                      <input required type="date" value={validityForm.validFrom} onChange={(e) => setValidityForm({ ...validityForm, validFrom: e.target.value })} className="w-full px-2 py-1 border rounded" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Fecha hasta (opc.)</label>
                      <input type="date" value={validityForm.validTo} onChange={(e) => setValidityForm({ ...validityForm, validTo: e.target.value })} className="w-full px-2 py-1 border rounded" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Notas</label>
                      <input value={validityForm.notes} onChange={(e) => setValidityForm({ ...validityForm, notes: e.target.value })} className="w-full px-2 py-1 border rounded" />
                    </div>
                    <button type="submit" disabled={detailBusy} className="px-3 py-2 bg-gray-800 text-white rounded disabled:opacity-50">
                      Agregar vigencia
                    </button>
                  </form>
                </section>

                <section>
                  <h3 className="text-lg font-medium mb-2">Desglose de cuota</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Conceptos fijos o % (sobre cuota o capital del crédito). Uno debe marcarse como “cierra la suma”.
                  </p>
                  <div className="space-y-2 mb-3 overflow-x-auto">
                    {chargeRows.map((row, idx) => (
                      <div key={idx} className="flex flex-wrap gap-2 items-center text-sm border rounded p-2">
                        <input
                          placeholder="Código"
                          value={row.code}
                          onChange={(e) => {
                            const next = [...chargeRows];
                            next[idx] = { ...next[idx], code: e.target.value };
                            setChargeRows(next);
                          }}
                          className="w-28 px-2 py-1 border rounded"
                        />
                        <input
                          placeholder="Nombre"
                          value={row.name}
                          onChange={(e) => {
                            const next = [...chargeRows];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setChargeRows(next);
                          }}
                          className="w-36 px-2 py-1 border rounded"
                        />
                        <select
                          value={row.valueMode}
                          onChange={(e) => {
                            const next = [...chargeRows];
                            next[idx] = { ...next[idx], valueMode: e.target.value as 'FIXED' | 'PERCENT' };
                            setChargeRows(next);
                          }}
                          className="px-2 py-1 border rounded"
                        >
                          <option value="PERCENT">%</option>
                          <option value="FIXED">Fijo</option>
                        </select>
                        <select
                          value={row.base}
                          disabled={row.valueMode === 'FIXED' || row.isBalancing}
                          onChange={(e) => {
                            const next = [...chargeRows];
                            next[idx] = { ...next[idx], base: e.target.value as 'INSTALLMENT' | 'LOAN_PRINCIPAL' };
                            setChargeRows(next);
                          }}
                          className="px-2 py-1 border rounded"
                        >
                          <option value="INSTALLMENT">Base: cuota</option>
                          <option value="LOAN_PRINCIPAL">Base: capital crédito</option>
                        </select>
                        <input
                          type="number"
                          step="any"
                          placeholder="Valor"
                          disabled={row.isBalancing}
                          value={row.value}
                          onChange={(e) => {
                            const next = [...chargeRows];
                            next[idx] = { ...next[idx], value: e.target.value };
                            setChargeRows(next);
                          }}
                          className="w-24 px-2 py-1 border rounded"
                        />
                        <label className="flex items-center gap-1 whitespace-nowrap">
                          <input
                            type="radio"
                            name="balancing-charge"
                            checked={row.isBalancing}
                            onChange={() => {
                              setChargeRows(chargeRows.map((r, i) => ({ ...r, isBalancing: i === idx })));
                            }}
                          />
                          Cierra suma
                        </label>
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={row.isActive}
                            onChange={(e) => {
                              const next = [...chargeRows];
                              next[idx] = { ...next[idx], isActive: e.target.checked };
                              setChargeRows(next);
                            }}
                          />
                          Activo
                        </label>
                        <button
                          type="button"
                          onClick={() => setChargeRows(chargeRows.filter((_, i) => i !== idx))}
                          className="text-red-600"
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setChargeRows([
                          ...chargeRows,
                          {
                            code: '',
                            name: '',
                            kind: 'CUSTOM',
                            valueMode: 'PERCENT',
                            base: 'INSTALLMENT',
                            value: '0',
                            isBalancing: chargeRows.length === 0,
                            isActive: true,
                          },
                        ])
                      }
                      className="px-3 py-1 border rounded text-sm"
                    >
                      + Concepto
                    </button>
                    <button type="button" onClick={seedSystemCharges} className="px-3 py-1 border rounded text-sm">
                      Seed interés/gastos/seguro/capital
                    </button>
                    <button disabled={detailBusy} onClick={saveCharges} className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50">
                      Guardar desglose
                    </button>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-medium mb-4">Simulador de cuotas</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Usa gracia ({detail.graceDays ?? 0} días), tipo {detail.planType ?? 'VARIABLE'}
                    {detail.fixedDueDay ? ` y día fijo ${detail.fixedDueDay}` : ''}.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-4">
                    {field('Monto', simForm.amount, (v) => setSimForm({ ...simForm, amount: v }), 'number')}
                    {field('Plazo (meses)', simForm.termMonths, (v) => setSimForm({ ...simForm, termMonths: v }), 'number')}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Inicio (opc.)</label>
                      <input
                        type="date"
                        value={simForm.startDate}
                        onChange={(e) => setSimForm({ ...simForm, startDate: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={simBusy}
                      onClick={runSimulation}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-md disabled:opacity-50"
                    >
                      {simBusy ? 'Calculando…' : 'Simular'}
                    </button>
                  </div>
                  {simResult && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 text-sm flex flex-wrap gap-4">
                        <span>Cuota: <strong>${simResult.pmt?.toLocaleString()}</strong></span>
                        <span>Total: <strong>${simResult.totalAmount?.toLocaleString()}</strong></span>
                        <span>Interés: <strong>${simResult.totalInterest?.toLocaleString()}</strong></span>
                        <span>
                          Gastos:{' '}
                          <strong>
                            $
                            {(
                              (simResult.schedule ?? []).reduce(
                                (s: number, r: any) => s + Number(r.feeAmount ?? 0),
                                0,
                              )
                            ).toLocaleString()}
                          </strong>
                        </span>
                      </div>
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left">#</th>
                            <th className="px-3 py-2 text-left">Vencimiento</th>
                            <th className="px-3 py-2 text-right">Capital</th>
                            <th className="px-3 py-2 text-right">Interés</th>
                            <th className="px-3 py-2 text-right">Gastos</th>
                            <th className="px-3 py-2 text-right">Cuota</th>
                            <th className="px-3 py-2 text-left">Desglose</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {(simResult.schedule ?? []).map((row: any) => {
                            const fee = Number(row.feeAmount ?? 0);
                            const chargeLines = (row.charges ?? []).map(
                              (c: any) => `${c.name}: $${Number(c.amount).toLocaleString()}`,
                            );
                            if (!chargeLines.length && fee > 0) {
                              chargeLines.push(`Admin: $${fee.toLocaleString()}`);
                            }
                            return (
                              <tr key={row.installmentNumber}>
                                <td className="px-3 py-2">{row.installmentNumber}</td>
                                <td className="px-3 py-2">{fmtDate(row.dueDate)}</td>
                                <td className="px-3 py-2 text-right">${row.principalAmount?.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right">${row.interestAmount?.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right">${fee.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right font-medium">${row.totalAmount?.toLocaleString()}</td>
                                <td className="px-3 py-2 text-xs text-gray-600">
                                  {chargeLines.join(' · ') || '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <section>
                  <h3 className="text-lg font-medium mb-4">Sucursales habilitadas</h3>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {branches.map((b) => (
                      <label key={b.id} className="flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={selectedBranches.includes(b.id)} onChange={() => toggleDetailBranch(b.id)} />
                        {b.code} — {b.name}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Sin selección = todas las sucursales.</p>
                  <button disabled={detailBusy} onClick={saveBranches} className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50">
                    Guardar sucursales
                  </button>
                </section>

                <section>
                  <h3 className="text-lg font-medium mb-4">Coeficientes por plazo</h3>
                  <div className="space-y-2 mb-4">
                    {coeffRows.map((row, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input
                          type="number"
                          placeholder="Meses"
                          value={row.termMonths}
                          onChange={(e) => {
                            const next = [...coeffRows];
                            next[idx] = { ...next[idx], termMonths: e.target.value };
                            setCoeffRows(next);
                          }}
                          className="w-24 px-2 py-1 border rounded text-sm"
                        />
                        <input
                          type="number"
                          step="0.0001"
                          placeholder="Coeficiente"
                          value={row.coefficient}
                          onChange={(e) => {
                            const next = [...coeffRows];
                            next[idx] = { ...next[idx], coefficient: e.target.value };
                            setCoeffRows(next);
                          }}
                          className="w-32 px-2 py-1 border rounded text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setCoeffRows(coeffRows.filter((_, i) => i !== idx))}
                          className="text-red-600 text-sm"
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCoeffRows([...coeffRows, { termMonths: '', coefficient: '1' }])}
                      className="px-3 py-1 border rounded text-sm"
                    >
                      + Fila
                    </button>
                    <button disabled={detailBusy} onClick={saveCoefficients} className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50">
                      Actualizar coeficientes
                    </button>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Códigos de descuento</h2>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setCodeBusy(true);
            setError(null);
            try {
              await apiService.createDiscountCode({
                code: codeForm.code,
                description: codeForm.description || undefined,
                percentOff: parseFloat(codeForm.percentOff),
              });
              setCodeForm({ code: '', description: '', percentOff: '10' });
              await load();
            } catch (e: any) {
              setError(e?.response?.data?.message ?? e.message);
            } finally {
              setCodeBusy(false);
            }
          }}
          className="mb-4 bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-4"
        >
          <input required placeholder="Código" value={codeForm.code} onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value })} className="px-3 py-2 border rounded-md" />
          <input placeholder="Descripción" value={codeForm.description} onChange={(e) => setCodeForm({ ...codeForm, description: e.target.value })} className="px-3 py-2 border rounded-md" />
          <input required type="number" min={0} max={100} step="0.1" placeholder="% descuento" value={codeForm.percentOff} onChange={(e) => setCodeForm({ ...codeForm, percentOff: e.target.value })} className="px-3 py-2 border rounded-md" />
          <button type="submit" disabled={codeBusy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Crear código</button>
        </form>
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Activo</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {discountCodes.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-2 text-sm font-mono">{c.code}</td>
                  <td className="px-4 py-2 text-sm">{c.description ?? '—'}</td>
                  <td className="px-4 py-2 text-sm text-right">{c.percentOff}%</td>
                  <td className="px-4 py-2 text-center">{c.isActive ? '●' : '○'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => apiService.setDiscountCodeActive(c.id, !c.isActive).then(load)}
                      className="text-sm text-blue-700 hover:underline"
                    >
                      {c.isActive ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
